import {
  type AddonType,
  type CartSubItem,
  type LocalCartAddon,
  type LocalCartItem,
  type MoneyInfo,
  type Ticket,
  type TicketType,
  type TranslatedText,
} from 'restful/tickets';
import {
  isIncludedCatalogueChild,
  isPaidAddon,
  subTicketTypeOrder,
} from 'restful/tickets/addon.utils';

/** Single child row for TicketInfoCard: included extras (Parking, Wallet) or paid addons. */
export type TicketInfoChildRow = {
  id: string;
  name: TranslatedText | null;
  ticketType?: TicketType | null;
  addonType?: AddonType | null;
  quantity?: number;
  totalPrice?: MoneyInfo | null;
};

/** Build child rows for a local cart item. Merges catalogue childItems with the local addon quantities. */
export const getLocalCartChildRows = (
  item: LocalCartItem,
  parentTicket: Ticket | undefined,
  getAddonTotalPrice: (catalogueItemId: string, quantity: number) => MoneyInfo,
): Array<TicketInfoChildRow> => {
  const addonById = new Map(
    item.addons?.map((addon) => [addon.catalogueItemId, addon]) ?? [],
  );
  const includedRows: Array<TicketInfoChildRow> = [];
  const paidRows: Array<TicketInfoChildRow> = [];

  parentTicket?.childItems?.forEach((child) => {
    if (isIncludedCatalogueChild(child.ticketType)) {
      includedRows.push({
        id: child.id,
        name: child.name,
        ticketType: child.ticketType,
        addonType: child.addonType,
      });
      return;
    }

    if (!isPaidAddon(child.ticketType, child.addonType)) {
      return;
    }

    const addon = addonById.get(child.id);
    addonById.delete(child.id);

    if (!addon || addon.quantity <= 0) {
      return;
    }

    paidRows.push(mapPaidAddonRow(addon, getAddonTotalPrice));
  });

  addonById.forEach((addon) => {
    if (
      !isPaidAddon(addon.ticketType, addon.addonType) ||
      addon.quantity <= 0
    ) {
      return;
    }

    paidRows.push(mapPaidAddonRow(addon, getAddonTotalPrice));
  });

  return [...sortIncludedChildRows(includedRows), ...paidRows];
};

/** Build child rows from API cart childItems. Included extras first, then paid addons. */
export const getApiCartChildRows = (
  childItems: Array<CartSubItem> | null | undefined,
): Array<TicketInfoChildRow> => {
  const includedRows: Array<TicketInfoChildRow> = [];
  const paidRows: Array<TicketInfoChildRow> = [];

  childItems?.forEach((child) => {
    if (isIncludedCatalogueChild(child.ticketType)) {
      includedRows.push({
        id: child.id,
        name: child.name,
        ticketType: child.ticketType,
        addonType: child.addonType,
      });
      return;
    }

    if (!isPaidAddon(child.ticketType, child.addonType)) {
      return;
    }

    paidRows.push({
      id: child.id,
      name: child.name,
      ticketType: child.ticketType,
      addonType: child.addonType,
      quantity: child.quantity,
      totalPrice: child.totalPrice,
    });
  });

  return [...sortIncludedChildRows(includedRows), ...paidRows];
};

const sortIncludedChildRows = (rows: Array<TicketInfoChildRow>) =>
  [...rows].sort(
    (a, b) =>
      subTicketTypeOrder(a.ticketType) - subTicketTypeOrder(b.ticketType),
  );

const mapPaidAddonRow = (
  addon: LocalCartAddon,
  getAddonTotalPrice: (catalogueItemId: string, quantity: number) => MoneyInfo,
): TicketInfoChildRow => ({
  id: addon.catalogueItemId,
  name: addon.name,
  ticketType: addon.ticketType,
  addonType: addon.addonType,
  quantity: addon.quantity,
  totalPrice: getAddonTotalPrice(addon.catalogueItemId, addon.quantity),
});
